import { useCallback, useEffect, useRef, useState } from 'react';
import { jsPDF } from 'jspdf';
import { Pencil, Square, Circle, Type, StickyNote, Undo2, Trash2, Download, FileDown, X } from 'lucide-react';
import { connectSocket } from '@/lib/socket';
import { whiteboardApi, type WbElement } from './whiteboard.api';
import { renderScene, BOARD_W, BOARD_H } from './render';

type Tool = 'pen' | 'rect' | 'ellipse' | 'text' | 'note';
const COLORS = ['#0f172a', '#ef4444', '#3b82f6', '#22c55e', '#eab308', '#a855f7'];
const NOTE_FILL = '#fde68a';
const uid = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.round(performance.now())}`);

export default function Whiteboard({ meetingId, onClose }: { meetingId: string; onClose: () => void }): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sceneRef = useRef<WbElement[]>([]);
  const draft = useRef<WbElement | null>(null);
  const drawing = useRef(false);
  const [tool, setTool] = useState<Tool>('pen');
  const [color, setColor] = useState(COLORS[1]);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const draw = useCallback(() => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    renderScene(ctx, draft.current ? [...sceneRef.current, draft.current] : sceneRef.current);
  }, []);

  const scheduleSave = useCallback(() => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => void whiteboardApi.save(meetingId, sceneRef.current).catch(() => undefined), 800);
  }, [meetingId]);

  const addElement = useCallback(
    (el: WbElement, broadcast = true) => {
      sceneRef.current = [...sceneRef.current, el];
      draw();
      if (broadcast) {
        connectSocket().emit('wb:element', { meetingId, element: el });
        scheduleSave();
      }
    },
    [draw, meetingId, scheduleSave],
  );

  // load + realtime
  useEffect(() => {
    whiteboardApi.get(meetingId).then((scene) => {
      sceneRef.current = scene;
      draw();
    });
    const socket = connectSocket();
    const onEl = (el: WbElement) => addElement(el, false);
    const onClear = () => {
      sceneRef.current = [];
      draw();
    };
    socket.on('wb:element', onEl);
    socket.on('wb:clear', onClear);
    return () => {
      socket.off('wb:element', onEl);
      socket.off('wb:clear', onClear);
    };
  }, [meetingId, draw, addElement]);

  const toBoard = (e: React.PointerEvent): { x: number; y: number } => {
    const r = canvasRef.current!.getBoundingClientRect();
    return { x: ((e.clientX - r.left) / r.width) * BOARD_W, y: ((e.clientY - r.top) / r.height) * BOARD_H };
  };

  const onDown = (e: React.PointerEvent) => {
    const p = toBoard(e);
    if (tool === 'text') {
      const text = window.prompt('Text:');
      if (text) addElement({ id: uid(), type: 'text', x: p.x, y: p.y, text, color, size: 24 });
      return;
    }
    if (tool === 'note') {
      const text = window.prompt('Sticky note:') ?? '';
      addElement({ id: uid(), type: 'note', x: p.x, y: p.y, w: 180, h: 140, text, color: NOTE_FILL });
      return;
    }
    drawing.current = true;
    if (tool === 'pen') draft.current = { id: uid(), type: 'path', points: [p], color, width: 3 };
    else draft.current = { id: uid(), type: tool, x: p.x, y: p.y, w: 0, h: 0, color };
  };

  const onMove = (e: React.PointerEvent) => {
    if (!drawing.current || !draft.current) return;
    const p = toBoard(e);
    const d = draft.current;
    if (d.type === 'path') d.points.push(p);
    else if (d.type === 'rect' || d.type === 'ellipse') {
      d.w = p.x - d.x;
      d.h = p.y - d.y;
    }
    draw();
  };

  const onUp = () => {
    if (draft.current) {
      const el = draft.current;
      draft.current = null;
      drawing.current = false;
      // ignore zero-size shapes / single-point paths
      const valid = el.type === 'path' ? el.points.length > 1 : Math.abs((el as any).w) > 3 || Math.abs((el as any).h) > 3;
      if (valid) addElement(el);
      else draw();
    }
  };

  const undo = () => {
    sceneRef.current = sceneRef.current.slice(0, -1);
    draw();
    scheduleSave();
  };
  const clearAll = () => {
    sceneRef.current = [];
    draw();
    connectSocket().emit('wb:clear', { meetingId });
    scheduleSave();
  };

  const exportCanvas = (): HTMLCanvasElement => {
    const c = document.createElement('canvas');
    c.width = BOARD_W;
    c.height = BOARD_H;
    renderScene(c.getContext('2d')!, sceneRef.current);
    return c;
  };
  const exportPng = () => {
    const url = exportCanvas().toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url;
    a.download = `whiteboard-${meetingId}.png`;
    a.click();
  };
  const exportPdf = () => {
    const png = exportCanvas().toDataURL('image/png');
    const pdf = new jsPDF({ orientation: 'landscape', unit: 'px', format: [BOARD_W, BOARD_H] });
    pdf.addImage(png, 'PNG', 0, 0, BOARD_W, BOARD_H);
    pdf.save(`whiteboard-${meetingId}.pdf`);
  };

  const TOOLS: [Tool, typeof Pencil][] = [
    ['pen', Pencil],
    ['rect', Square],
    ['ellipse', Circle],
    ['text', Type],
    ['note', StickyNote],
  ];

  return (
    <div className="absolute inset-0 z-30 flex flex-col bg-slate-100">
      <div className="flex items-center gap-2 border-b bg-white px-3 py-2 shadow-sm">
        {TOOLS.map(([t, Icon]) => (
          <button key={t} className={`rounded-lg p-2 ${tool === t ? 'bg-brand-600 text-white' : 'hover:bg-slate-100'}`} onClick={() => setTool(t)}>
            <Icon size={18} />
          </button>
        ))}
        <span className="mx-1 h-6 w-px bg-slate-200" />
        {COLORS.map((c) => (
          <button key={c} className={`h-6 w-6 rounded-full border ${color === c ? 'ring-2 ring-brand-500' : ''}`} style={{ background: c }} onClick={() => setColor(c)} />
        ))}
        <span className="mx-1 h-6 w-px bg-slate-200" />
        <button className="rounded-lg p-2 hover:bg-slate-100" onClick={undo} title="Undo"><Undo2 size={18} /></button>
        <button className="rounded-lg p-2 text-red-600 hover:bg-slate-100" onClick={clearAll} title="Clear"><Trash2 size={18} /></button>
        <div className="ml-auto flex items-center gap-2">
          <button className="btn-ghost border" onClick={exportPng}><Download size={16} /> PNG</button>
          <button className="btn-ghost border" onClick={exportPdf}><FileDown size={16} /> PDF</button>
          <button className="rounded-lg p-2 hover:bg-slate-200" onClick={onClose}><X size={18} /></button>
        </div>
      </div>
      <div className="flex flex-1 items-center justify-center overflow-auto p-4">
        <canvas
          ref={canvasRef}
          width={BOARD_W}
          height={BOARD_H}
          className="max-h-full max-w-full touch-none rounded-lg border bg-white shadow"
          style={{ aspectRatio: `${BOARD_W} / ${BOARD_H}` }}
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerLeave={onUp}
        />
      </div>
    </div>
  );
}
